// External Imports
import { useEffect, useState } from "react";
import { MdAdd, MdClose } from "react-icons/md";
import { TbChevronDown, TbChevronUp } from "react-icons/tb";

// Internal Imports
import { requestAdminAPI } from "../../api/posh-api";
import { isoDateToString } from "../../util/format-date";
import { toTitleCase, truncate } from "../../util/format-text";
import Heading from "./Heading";
import NoRecordsFound from "./NoRecordsFound";
import Modal from "./Modal";
import LoadingCard from "./LoadingCard";
import PersonCard from "./PersonCard";
import LinkPair from "./LinkPair";
import LoadingSpinner from "./LoadingSpinner";
import uploadFile from "../../util/upload-file";

/** ========================================================================
 * * Notes Component
 * This component is used to display notes for a specific record and
 * allows the user to add new notes.
 * ========================================================================== */

export default function Notes({ table, id, show: defaultShow = false }) {
  // Initialize States
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(defaultShow);
  const [add, setAdd] = useState(false);
  const [selected, setSelected] = useState(null);

  /* =================== FETCH NOTES =================== */
  const fetchNotes = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("filter", {
        table: "note",
        filters: [`${table}Id: { contains: "${id}" }`],
        columns: [
          `employee { id, name, photo }`,
        ],
      });
      setNotes(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* =================== ADD NOTE =================== */
  const addNoteHandler = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const note = event.target.note.value;
      const attachment = event.target.attachment.files[0]
        ? await uploadFile(
            `${table}s/${id}/${new Date().getTime()}`,
            event.target.attachment.files[0]
          )
        : null;

      // Send Request
      await requestAdminAPI("add-note", {
        table,
        id,
        note,
        attachment,
      });
      setAdd(false);
      if (show) fetchNotes();
      else setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Fetch Notes on Component Show
  useEffect(() => {
    if (show) fetchNotes();
  }, [show]);

  useEffect(() => {
    if (defaultShow) fetchNotes();
  }, [id]);

  return (
    <div className="grid gap-4">
      <NoteModal note={selected} setSelected={setSelected} table={table} />
      <AddNoteModal
        open={add}
        setOpen={setAdd}
        table={table}
        loading={loading}
        submitHandler={addNoteHandler}
      />
      <Heading
        title="Notes"
        text="Each record has individual notes. To add a note click the '+' icon on the right. Notes cannot be deleted once submitted."
        onClick={() => setShow(!show)}
        isBackArrow={false}
      >
        <MdAdd onClick={() => setAdd(true)} className="icon" />
        {show ? (
          <TbChevronUp className="icon" onClick={() => setShow(false)} />
        ) : (
          <TbChevronDown className="icon" onClick={() => setShow(true)} />
        )}
      </Heading>
      {loading ? (
        <LoadingCard columns={3} max={1} />
      ) : (
        show &&
        (notes.length > 0 ? (
          <div className="list max-h-[280px]">
            {notes
              .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated))
              .map((note, idx) => (
                <NoteCard key={idx} note={note} setSelected={setSelected} />
              ))}
          </div>
        ) : (
          <NoRecordsFound />
        ))
      )}
    </div>
  );
}

/** ========================================================================
 * * NOTE CARD
 * ========================================================================== */
const NoteCard = ({ note, setSelected }) => {
  return (
    <div
      onClick={() => setSelected(note)}
      className="grid text-sm md:grid-cols-5 pill"
    >
      <p className="text-stone-400">
        {isoDateToString(note.dateCreated, "MM/dd/yyyy hh:mm a")}
      </p>
      <p className="md:col-span-3">{truncate(note.note, 80)}</p>
      <p className="ml-auto text-stone-400">{note.employee?.name}</p>
    </div>
  );
};

/** ========================================================================
 * * NOTE MODAL
 * ========================================================================== */
const NoteModal = ({ note, setSelected, table }) => {
  return (
    <Modal open={note ? true : false} setOpen={setSelected}>
      <div className="modal text-sm lg:w-[40vw] gap-6">
        <Heading
          title={`Note on ${toTitleCase(table)}`}
          isBackArrow={false}
          hideHR={true}
          id={note?.id}
        >
          <MdClose className="icon" onClick={() => setSelected()} />
        </Heading>
        <textarea
          className="text-base text-stone-200 form-input-disabled"
          rows={4}
          disabled={true}
          value={note?.note}
        />
        <div className="flex justify-between space-x-4">
          <LinkPair label="Attachment" link={note?.attachment} />
          <PersonCard
            label="Author:"
            name={note?.employee?.name}
            secondary={isoDateToString(note?.dateCreated, "MM/dd/yyyy hh:mm a")}
            image={note?.employee?.photo}
            link={`/employees/info?id=${note?.employee?.id}`}
          />
        </div>
      </div>
    </Modal>
  );
};

/** ========================================================================
 * * ADD NOTE MODAL
 * ========================================================================== */
const AddNoteModal = ({ open, setOpen, table, loading, submitHandler }) => {
  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal">
        <Heading
          title={`Add Note on ${toTitleCase(table)}`}
          isBackArrow={false}
          hideHR={true}
        >
          <MdClose onClick={() => setOpen(false)} className="icon" />
        </Heading>
        <form onSubmit={submitHandler} className="grid gap-4 text-sm">
          <textarea
            type="text"
            name="note"
            className="w-full form-input"
            placeholder="Enter your note here..."
            rows={5}
            required
          />
          <div className="grid items-center grid-cols-2 gap-4">
            <LinkPair
              label="Attachment"
              edit={true}
              inputOptions={{
                type: "file",
                accept: "image/*, application/pdf",
                multiple: false,
                name: "attachment",
              }}
            />
          </div>
          {loading ? (
            <div className="mx-auto">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div onClick={() => setOpen(false)} className="btn-secondary">
                Cancel
              </div>
              <button type="submit" className="btn-primary">
                Confirm
              </button>
            </div>
          )}
        </form>
      </div>
    </Modal>
  );
};
